import {
    Box,
    CardContent,
    Typography,
    TextField,
    Checkbox,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    Button,
    CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid2";
import ChildCard from "src/components/shared/ChildCard";
import { useSelector, useDispatch } from "react-redux";
import { GenderType, titles } from "src/utils/common";
import CustomDatePicker from "src/components/form/theme-elements/CustomDatePicker";
import dayjs from "dayjs";
import {
    setGuestInfoChanged,
    updateReservationGuestInformation,
    updateGuestTitle,
    updateGuestFirstName,
    updateGuestLastName,
    updateGuestDateOfBirth,
    updateGuestCountry,
    updateGuestLeadStatus,
    updateAllGuestCountries,
    updateGuestGender,
} from "src/store/reservations/slices/ReservationsManagerSlice";
import { useMsal } from "@azure/msal-react";
import SearchCriteria from "src/components/shared/search-criteria/SearchCriteria";
import { fetchCountries } from "src/store/countries/CountryManagerSlice";
import DialogContentComponent from "src/components/common/DialogContentComponent";
import { getMinMaxDatesForGuest } from "src/store/reservations/actions/ReservationOverviewActions";
import { providerHelpers } from "src/utils/providerConfig";

const ViewGuestInformation = () => {
    const { reservationOverview } = useSelector((state) => state.reservationsSummaryReducer);
    const { isGuestInfoChanged } = useSelector((state) => state.reservationsSummaryReducer);
    const dispatch = useDispatch();
    const { instance, accounts } = useMsal();
    const [error, setError] = useState(null);
    const [update, setUpdate] = useState(false);
    const [loading, setLoading] = useState(false);
    const [countries, setCountries] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(false);

    const IsRequiredGuestInfo = providerHelpers.requiresGuestInfo(reservationOverview?.Provider);

    useEffect(() => {
        const loadCountries = async () => {
            setLoadingCountries(true);
            try {
                const result = await dispatch(fetchCountries(instance, accounts));
                if (result) {
                    setCountries(result);
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoadingCountries(false);
            }
        };
        loadCountries();
    }, [dispatch, instance, accounts]);

    if (!reservationOverview || !reservationOverview.Hotels || !reservationOverview.Pax) {
        return <Typography p={2}>No guest information available.</Typography>;
    }

    const getCountryByCode = (code) => {
        if (!code) return null;
        return countries.find(c => c.Code === code) || null;
    };

    const handleTitleChange = (guest, title) => {
        dispatch(setGuestInfoChanged(true));
        dispatch(updateGuestTitle({ guest, title }));
    };

    const handleGenderChange = (guest, gender) => {
        dispatch(setGuestInfoChanged(true));
        dispatch(updateGuestGender({ guest, gender }));
    };

    const handleFirstNameChange = (guest, firstName) => {
        dispatch(setGuestInfoChanged(true));
        dispatch(updateGuestFirstName({ guest, firstName }));
    };

    const handleLastNameChange = (guest, lastName) => {
        dispatch(setGuestInfoChanged(true));
        dispatch(updateGuestLastName({ guest, lastName }));
    };

    const handleDateOfBirthChange = (guest, date) => {
        dispatch(setGuestInfoChanged(true));
        const dateOfBirth = date ? date.toDate() : null;
        dispatch(updateGuestDateOfBirth({ guest, dateOfBirth }));
    };

    const handleCountryChange = (guest, country) => {
        dispatch(setGuestInfoChanged(true));
        
        const noCountriesSet = !reservationOverview.Pax.some(g => g.CountryCode);
        
        if (noCountriesSet) {
            dispatch(updateAllGuestCountries({ country }));
        } else {
            dispatch(updateGuestCountry({ guest, country }));
        }
    };

    const handleLeadGuestChange = (guest, isLead) => {
        dispatch(setGuestInfoChanged(true));
        dispatch(updateGuestLeadStatus({ guest, isLead }));
    };

    const handleUpdateGuestInformation = async () => {
        setLoading(true);
        setError(null);
        setUpdate(false);

        const requestPayload = reservationOverview.Pax.map((guest) => ({
            Title: guest.Title,
            FirstName: guest.FirstName,
            LastName: guest.LastName,
            DateOfBirth: guest.DateOfBirth
                ? dayjs(guest.DateOfBirth).isValid()
                    ? dayjs(guest.DateOfBirth).toISOString()
                    : null
                : null,
            LeadGuest: guest.LeadGuest,
            Gender: guest.Gender || null,
            PaxType: guest.PaxType,
            PaxNumber: guest.PaxNumber,
            CountryCode: guest.CountryCode || null
        }));

        try {
            await dispatch(
                updateReservationGuestInformation(
                    instance,
                    accounts,
                    reservationOverview.Id,
                    requestPayload
                )
            );

            setUpdate(true);
            dispatch(setGuestInfoChanged(false));
        } catch (error) {
            setError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setError(null);
        setUpdate(null);
    };

    const getGuestNumberByType = (guest) => {
        const guestsOfSameType = reservationOverview.Pax.filter(
            g => g.PaxType === guest.PaxType
        );
        const indexInType = guestsOfSameType.findIndex(g => g.PaxNumber === guest.PaxNumber);
        return indexInType + 1;
    };

    const canBeLeadGuest = (guest) => {
        return providerHelpers.canBeLeadGuest(reservationOverview?.Provider, guest.PaxType);
    };

    return (
        <CardContent>
            <DialogContentComponent
                error={error}
                handleClose={handleClose}
                title={update ? "Update" : ""}
                message={update ? "Your guest information has been successfully Updated." : ""}
            />
            <Grid container>
                <Grid item size={12}>
                    <Box>
                        {reservationOverview.Pax.map((guest) => {
                            const countryValue = guest.Country || getCountryByCode(guest.CountryCode);
                            const { minDate, maxDate } = getMinMaxDatesForGuest(guest);

                            return (
                                <ChildCard key={guest.PaxNumber}>
                                    <Grid container spacing={1} justifyContent="center" alignItems="center">
                                        <Grid item size={12}>
                                            <Grid container spacing={2} direction="row" justifyContent="center" alignItems="center">
                                                <Grid item size={1.3}>
                                                    <Typography variant="body1" color="text.secondary" align="inherit">
                                                        Title
                                                    </Typography>
                                                    <FormControl fullWidth size="small">
                                                        <Select
                                                            value={guest.Title || ""}
                                                            onChange={(e) => handleTitleChange(guest, e.target.value)}
                                                            variant="outlined"
                                                            disabled={IsRequiredGuestInfo}
                                                        >
                                                            <MenuItem value="">
                                                                <em>None</em>
                                                            </MenuItem>
                                                            {titles.map((title) => (
                                                                <MenuItem key={title} value={title}>
                                                                    {title}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid item size={2}>
                                                    <Typography variant="body1" color="text.secondary" align="inherit">
                                                        First Name
                                                    </Typography>
                                                    <TextField
                                                        value={guest.FirstName || ""}
                                                        onChange={(e) => handleFirstNameChange(guest, e.target.value)}
                                                        placeholder={`${guest.PaxType} ${getGuestNumberByType(guest)} - First Name`}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        disabled={IsRequiredGuestInfo}
                                                    />
                                                </Grid>

                                                <Grid item size={2}>
                                                    <Typography variant="body1" color="text.secondary" align="inherit">
                                                        Last Name
                                                    </Typography>
                                                    <TextField
                                                        value={guest.LastName || ""}
                                                        onChange={(e) => handleLastNameChange(guest, e.target.value)}
                                                        placeholder={`${guest.PaxType} ${getGuestNumberByType(guest)} - Last Name`}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        disabled={IsRequiredGuestInfo}
                                                    />
                                                </Grid>                                                

                                                <Grid item size={1.4}>
                                                    <Typography variant="body1" color="text.secondary" align="inherit">
                                                        Gender
                                                    </Typography>
                                                    <FormControl fullWidth size="small">
                                                        <Select
                                                            value={guest.Gender || ""}
                                                            onChange={(e) => handleGenderChange(guest, e.target.value)}
                                                            variant="outlined"
                                                            disabled={IsRequiredGuestInfo}
                                                        >
                                                            <MenuItem value="">
                                                                <em>None</em>
                                                            </MenuItem>
                                                            {GenderType.map((gender) => (
                                                                <MenuItem key={gender} value={gender}>
                                                                    {gender}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid item size={2.2}>
                                                    <Typography variant="body1" color="text.secondary" align="inherit">
                                                        Date of Birth
                                                    </Typography>
                                                    <CustomDatePicker
                                                        value={guest.DateOfBirth ? dayjs(guest.DateOfBirth) : null}
                                                        onChange={(date) => handleDateOfBirthChange(guest, date)}
                                                        openTo="year"
                                                        minDate={minDate}
                                                        maxDate={maxDate}
                                                        disabled={IsRequiredGuestInfo}
                                                    />
                                                </Grid>

                                                <Grid item size={2.1}>
                                                    <Typography variant="body1" color="text.secondary">
                                                        Country
                                                    </Typography>
                                                    <SearchCriteria
                                                        label="Name"
                                                        id={`country-select-${guest.PaxNumber}`}
                                                        setValue={(newValue) => handleCountryChange(guest, newValue)}
                                                        setOptionsData={countries}
                                                        selectedValue={countryValue}
                                                        loading={loadingCountries}
                                                        disabled={IsRequiredGuestInfo}
                                                    />
                                                </Grid>

                                                <Grid item size={1}>
                                                    <Typography variant="body1" color="text.secondary">
                                                        Lead Guest
                                                    </Typography>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={guest.LeadGuest}
                                                                onChange={(e) => handleLeadGuestChange(guest, e.target.checked)}
                                                                size="small"
                                                                disabled={IsRequiredGuestInfo || !canBeLeadGuest(guest)}
                                                            />
                                                        }
                                                        sx={{
                                                            display: "contents",
                                                            alignItems: "center",
                                                            justifyContent: "center"
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </ChildCard>
                            );
                        })}
                    </Box>

                    <Box sx={{ p: 1, display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleUpdateGuestInformation}
                            disabled={loading || !isGuestInfoChanged || IsRequiredGuestInfo}
                        >
                            Update All Guest Information
                            {loading && <CircularProgress size={20} sx={{ ml: 1 }} color="inherit" />}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </CardContent>
    );
};

export default ViewGuestInformation;